import React from 'react';
import { Flex, Box } from 'rebass';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { maxWidth as maxWidthFn } from 'styled-system';
import { Trans } from '@lingui/macro';

import { Account } from '@features/common';
import {
  Avatar,
  Typography,
  Spinner,
  File,
  Menu,
  IconButton,
  Previewer,
  Tooltip,
} from '@oca/ui';
import { Person, Pencil, Delete, MenuDots } from '@oca/icons';
import { deleteCommentRequesting, setCommentText } from '../model/comment-flow';

// eslint-disable-next-line react/prop-types
export const Comment = ({ comment, maxWidth }) => {
  const [deletingIsOn, setDeleteStatus] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const isEdited = dayjs(new Date(comment.updatedAt)).isAfter(
    new Date(comment.createdAt),
  );

  return (
    <Flex
      backgroundColor="secondary"
      style={{ borderRadius: 12 }}
      padding={2}
      paddingX={3}
      mb={20}
    >
      <Link to={`/hr/employees/${comment.author.username}`}>
        <Avatar src={comment.author.avatar} size={30} mr={10}>
          <Person color="#aaa" />
        </Avatar>
      </Link>
      {/* minWidth={0} lets this flex item shrink below the slider's intrinsic
          width, so the attachments slider is constrained to the comment width
          instead of overflowing it. */}
      <Box flex={1} minWidth={0}>
        <Flex alignItems="center">
          <Box mr={3}>
            <Link
              to={`/hr/employees/${comment.author.username}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography color="#344054" fontWeight={500}>
                {`${comment.author.firstName} ${comment.author.lastName}`}
              </Typography>
            </Link>
          </Box>
          <Typography as="div" variant="caption" color="textSecondary">
            {dayjs(new Date(comment.createdAt)).format('LLL')}
            &nbsp;
            {isEdited && (
              <Tooltip
                title={dayjs(new Date(comment.editedAt)).format('LLL')}
                placement="bottom"
              >
                (edited)
              </Tooltip>
            )}
          </Typography>
          <Account
            render={({ user }) => {
              return (
                user.id === comment.author.id && (
                  <Flex ml="auto">
                    {deletingIsOn ? (
                      <Flex
                        style={{ height: 30, width: 30 }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Spinner size={16} />
                      </Flex>
                    ) : (
                      <Menu
                        placement="bottom-start"
                        anchor={
                          <IconButton size="small" padding="5px" color="text">
                            <MenuDots />
                          </IconButton>
                        }
                      >
                        <Menu.Item
                          icon={<Pencil />}
                          onClick={() => setCommentText(comment)}
                        >
                          <Typography>
                            <Trans>Edit</Trans>
                          </Typography>
                        </Menu.Item>
                        <Menu.Item
                          icon={<Delete />}
                          onClick={() => {
                            setDeleteStatus(true);
                            deleteCommentRequesting(comment.id).then(() => {
                              setDeleteStatus(false);
                            });
                          }}
                        >
                          <Typography>
                            <Trans>Delete</Trans>
                          </Typography>
                        </Menu.Item>
                      </Menu>
                    )}
                  </Flex>
                )
              );
            }}
          />
        </Flex>

        <Typography color="textSecondary" gutterBottom>
          {replaceLinks(comment.text)}
        </Typography>

        {comment.attachmentFiles.length > 0 && (
          <CommentAttachmentsContainer width={1} maxWidth={maxWidth}>
            {preview && (
              <Previewer {...preview} onClose={() => setPreview(null)} />
            )}

            <AttachmentsPager
              attachments={comment.attachmentFiles}
              onPreview={({ fileName, file, fileExt }) =>
                setPreview({ fileName, file, fileExt })
              }
            />
          </CommentAttachmentsContainer>
        )}
      </Box>
    </Flex>
  );
};

const PAGE_SIZE = 2;

// Shows exactly two attachments at a time with prev/next paging. Built from
// plain flex (no slider lib) so it can never measure itself wider than its
// container: an unbroken min-width:0 chain (down to File's noWrap filename)
// lets long names ellipsize instead of widening the comment / card / page.
function AttachmentsPager({ attachments, onPreview }) {
  const [page, setPage] = React.useState(0);

  const pageCount = Math.max(Math.ceil(attachments.length / PAGE_SIZE), 1);
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;

  const hasArrows = attachments.length > PAGE_SIZE;
  const canPrev = safePage > 0;
  const canNext = safePage < pageCount - 1;

  // Always render PAGE_SIZE slots; the trailing slot is empty on an odd last
  // page so the 50/50 split never collapses to one full-width chip.
  const slots = [];
  for (let i = 0; i < PAGE_SIZE; i += 1) {
    slots.push(attachments[start + i] || null);
  }

  return (
    <PagerRow>
      {hasArrows && (
        <PagerArrow
          type="button"
          aria-label="Previous attachments"
          disabled={!canPrev}
          onClick={() => setPage(p => Math.max(p - 1, 0))}
        >
          {'‹'}
        </PagerArrow>
      )}

      <PagerTrack>
        {slots.map((att, i) => (
          <PagerSlot key={att ? att.id : `empty-${i}`}>
            {att && (
              <File
                fullWidth
                name={att.fileName}
                ext={att.fileExt}
                size={att.size}
                onClick={() =>
                  onPreview({
                    fileName: att.fileName,
                    file: att.file,
                    fileExt: att.fileExt,
                  })
                }
              />
            )}
          </PagerSlot>
        ))}
      </PagerTrack>

      {hasArrows && (
        <PagerArrow
          type="button"
          aria-label="Next attachments"
          disabled={!canNext}
          onClick={() => setPage(p => Math.min(p + 1, pageCount - 1))}
        >
          {'›'}
        </PagerArrow>
      )}
    </PagerRow>
  );
}

AttachmentsPager.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fileName: PropTypes.string,
      fileExt: PropTypes.string,
      file: PropTypes.string,
      size: PropTypes.number,
    }),
  ).isRequired,
  onPreview: PropTypes.func.isRequired,
};

const CommentAttachmentsContainer = styled(Box)`
  ${maxWidthFn};
  /* Outer clamp: never let the attachments grow the comment. */
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

/* Row: [arrow] [track] [arrow]. The fixed-size arrows sit outside the track's
   basis; min-width:0 lets the row shrink below its children's intrinsic width. */
const PagerRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  gap: 0.25rem;
`;

/* Holds the two slots and takes only the space left by the arrows. */
const PagerTrack = styled.div`
  flex: 1 1 0;
  display: flex;
  align-items: stretch;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
  gap: 0.5rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
`;

/* flex:1 1 0 → true 50/50 (basis 0 so long names can't bias the split and the
   gap can't push past 100%); min-width:0 defeats min-width:auto. The `& *` rule
   injects min-width:0 into File's inner parts (FileWrapper + MetaContainer,
   which we can't edit) so the noWrap filename ellipsizes instead of widening
   the chip. Safe: the file icon is sized via font-size, not width. */
const PagerSlot = styled.div`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;

  & > * {
    max-width: 100%;
  }
  & * {
    min-width: 0;
  }
`;

const PagerArrow = styled.button`
  flex: 0 0 auto;
  align-self: center;
  width: 1.75rem;
  height: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #e4e7ec;
  border-radius: 9999px;
  background: #fff;
  color: #344054;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: #f2f4f7;
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

// Replace links in text to HTML links
const replaceLinks = text => {
  const matched = urlsAsArray(text);

  if (matched) {
    const components = linkLessTextArray(text, matched).map((el, i) => {
      return (
        <React.Fragment>
          <Typography as="span">{el}</Typography>
          <ExternalLink url={matched[i]} />
        </React.Fragment>
      );
    });

    return components;
  }
  return <Typography as="span">{text}</Typography>;
};

const urlsAsArray = text =>
  (text || '').match(/([^\S]|^)(((https?\:\/\/)|(www\.))(\S+))/gi);

const linkLessTextArray = (text, urls) => {
  let linkLess = text;
  urls.forEach(el => {
    linkLess = linkLess.replace(el, ',');
  });
  return linkLess.split(',');
};

const ExternalLink = ({ url }) => {
  const trimmedUrl = url.trim();
  return url ? (
    <React.Fragment>
      {' '}
      <a
        href={!url.match('^https?://') ? `http://${trimmedUrl}` : trimmedUrl}
        target="_blank"
        style={{
          color: 'primary',
          textDecoration: 'none',
        }}
      >
        {trimmedUrl}
      </a>
    </React.Fragment>
  ) : (
    ''
  );
};

ExternalLink.propTypes = {
  url: PropTypes.string,
};

ExternalLink.defaultProps = {
  url: '',
};
